import { createApp } from '@shopify/app-bridge';
import { NavMenu } from '@shopify/app-bridge-react';
import { getSessionToken } from '@shopify/app-bridge-utils';
import { AppProvider } from '@shopify/polaris';
import translations from '@shopify/polaris/locales/en.json';
import { PostHogProvider } from 'posthog-js/react';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import './App.css';
import { NavigationLink } from './components';
import AppInitializer from './components/AppInitializer';
import OnboardingGuide from './components/OnboardingGuide';
import { embeddedNavLinksWithoutReview, Environment, LocalStorageKey, PATH, SessionStorageKey } from './constants';
import { CONSTANT, convertQueryStringToObject, setLocalStorageItem } from './helpers';
import { useAffiliate, usePostHogRecord } from './hooks';
import useConfirmationNavigate from './hooks/useConfirmationNavigate';
import { useUpgradeTracking } from './hooks/useUpgradeTracking';
import useScript from './hooks/useScript';
import useCrispProactive from './hooks/useCrispProactive';
import authSlice, { sessionTokenSelector, tokenSelector } from './redux/features/auth.slice';
import AppRoutes from './routes';

function App() {
  useScript();
  useCrispProactive();
  useAffiliate();
  usePostHogRecord();
  const dispatch = useDispatch();
  const navigate = useConfirmationNavigate();
  const { navigateToPlan } = useUpgradeTracking();
  const location = useLocation();
  const { search } = location;
  const { token } = useSelector(tokenSelector);
  const { token: sessionToken } = useSelector(sessionTokenSelector);
  const searchToObj = convertQueryStringToObject(search);
  const isOpenFromCms = searchToObj?.scope === 'omegatheme' || token?.scope === 'omegatheme';
  const isProduction = process.env.REACT_APP_ENV === Environment.Production;
  const isProductionAndNotOpenFromCms = isProduction && !isOpenFromCms;
  const configApp = {
    apiKey: process.env.REACT_APP_API_KEY || '',
    host:
      new URLSearchParams(search).get('host') ||
      window.btoa('admin.shopify.com/store/' + token?.shop.split('.')[0]).replaceAll('=', ''),
    forceRedirect: isProductionAndNotOpenFromCms,
  };

  const appContent = (
    <AppProvider i18n={translations} linkComponent={NavigationLink}>
      {process.env.REACT_APP_ENV !== Environment.Test && CONSTANT.isEmbedded && (
        <NavMenu>
          {embeddedNavLinksWithoutReview.map(({ label, destination }) => {
            const isSubscriptionPlansLink = destination === PATH.PLAN;
            return (
              <NavLink
                id={destination}
                key={destination}
                to={destination}
                onClick={() => {
                  if (isSubscriptionPlansLink) {
                    navigateToPlan({
                      trigger_type: 'nav_link',
                      trigger_element: 'Subscription Plans',
                      feature: 'navigation',
                    });
                  } else {
                    navigate(destination);
                  }
                }}
              >
                {label}
              </NavLink>
            );
          })}
        </NavMenu>
      )}
      <AppInitializer>
        <AppRoutes />
        <OnboardingGuide />
      </AppInitializer>
    </AppProvider>
  );

  // AUTHENTICATION
  useMemo(() => {
    if (searchToObj?.shop && searchToObj.shop !== token?.shop) {
      if (!CONSTANT.isEmbedded) {
        sessionStorage.setItem(
          SessionStorageKey.AUTH,
          JSON.stringify({
            shop: searchToObj.shop,
            token: { ...searchToObj, urlParams: JSON.stringify(searchToObj) },
            isAuth: true,
          }),
        );
      }
    } else if (!CONSTANT.isEmbedded && sessionStorage.getItem(SessionStorageKey.AUTH)) {
      const auth = JSON.parse(sessionStorage.getItem(SessionStorageKey.AUTH) || '{}');
      dispatch(authSlice.actions.handleAuth(auth));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // urlParams are HMAC-signed by Shopify on every app load. Refresh the persisted copy even for the
    // same shop, otherwise requests that fall back to the stored value keep an old signature that the
    // backend rejects once the previous client secret is retired.
    const isNewShop = searchToObj?.shop !== token?.shop;
    const isStaleUrlParams = JSON.stringify(searchToObj) !== token?.urlParams;
    if (searchToObj?.shop && (isNewShop || isStaleUrlParams)) {
      setLocalStorageItem(LocalStorageKey.OMEGA_SHOP, searchToObj.shop);
      setLocalStorageItem(LocalStorageKey.LOGIN_QUERY_STRING, search);
      dispatch(
        authSlice.actions.handleAuth({
          shop: searchToObj.shop,
          token: { ...searchToObj, urlParams: JSON.stringify(searchToObj) },
          isAuth: true,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (CONSTANT.isEmbedded && !sessionToken) {
      const getToken = async () => {
        try {
          const app = createApp(configApp);
          const sessionToken = await getSessionToken(app);
          dispatch(authSlice.actions.handleGetSessionToken({ token: sessionToken, expiredTime: Date.now() + 60 * 1000 }));
        } catch (error) {
          console.log('Error when get session token', error);
        }
      };
      getToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isProductionAndNotOpenFromCms ? (
    <PostHogProvider
      apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY || ''}
      options={{
        api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST || '',
        defaults: '2025-05-24',
        enable_heatmaps: true,
      }}
    >
      {appContent}
    </PostHogProvider>
  ) : (
    appContent
  );
}

export default App;
